import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { useValidateTakeProfitMutation } from '@/store/orders/takeProfitOrder/api/index.ts';
import { selectTakeProfitTile } from '@/store/orders/takeProfitOrder/selectors.ts';
import type { TakeProfitOrder } from '@/store/orders/takeProfitOrder/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

export type TakeProfitContextContent = {
  tileId: string;
  isChecking: boolean;
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
  validateTakeProfit: TakeProfitValidateFn;
};

type TakeProfitValidateFn = (
  updatedProperties: Partial<TakeProfitOrder['properties']>,
) => Promise<
  | { data: ValidateOrderResponse; error: undefined }
  | { data: undefined; error: FetchBaseQueryError | SerializedError }
  | undefined
>;

type TakeProfitModifyingFieldContextContent = {
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
};

const TakeProfitTileIdContext = createContext('');
const TakeProfitIsCheckingContext = createContext(false);
const TakeProfitValidateContext = createContext<TakeProfitValidateFn>(async (_updatedProperties) => undefined);
const TakeProfitModifyingFieldContext = createContext<TakeProfitModifyingFieldContextContent>({
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const TakeProfitContext = createContext<TakeProfitContextContent>({
  tileId: '',
  isChecking: false,
  validateTakeProfit: async (_updatedProperties: Partial<TakeProfitOrder['properties']>) => undefined,
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const TakeProfitProvider = (props: { tileId: string; children: ReactNode }) => {
  const { children, tileId } = props;
  const [isChecking, setIsChecking] = useState(false);
  const [isModifyingField, setIsModifyingField] = useState(false);

  const [validateTakeProfitMutation] = useValidateTakeProfitMutation({
    selectFromResult: () => ({}),
  });
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const { tile, preferencesNotificationEmails } = useAppSelector((state) => {
    const userPreferences = state.userPreferences;

    if (isNotDefined(userPreferences)) {
      console.error('[useValidateTakeProfit] User preferences not found in state.');
      return {};
    }

    const tile = selectTakeProfitTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[useValidateTakeProfit] Tile with id ${tileId} not found in state.`);
      return {};
    }

    if (tile.type !== 'TakeProfitOrder') {
      console.error(`[useValidateTakeProfit] Tile with id ${tileId} is not a TakeProfitOrder.`);
      return {};
    }

    return {
      tile,
      preferencesNotificationEmails: userPreferences.splitNotifications
        ? userPreferences.splitNotificationsEmailsOrders
        : userPreferences.emails,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one handleValidateTakeProfit request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const handleValidateTakeProfit = useCallback(
    async (updatedProperties: Partial<TakeProfitOrder['properties']>) => {
      return queue.add(async () => {
        if (isNotDefined(tile)) {
          console.error(`[handleValidateTakeProfit] Tile with id ${tileId} not found in state.`);
          return null;
        }

        const currency1 = updatedProperties.currencyPair?.currency1 ?? tile.properties.currencyPair?.currency1;
        const currency2 = updatedProperties.currencyPair?.currency2 ?? tile.properties.currencyPair?.currency2;

        if (isNotDefined(clientId)) {
          console.error('[handleValidateTakeProfit] client id is not defined');
          return null;
        }

        if (isNotDefined(currency1) || isNotDefined(currency2)) {
          console.error(`[handleValidateTakeProfit] Invalid currency pair: ${currency1}/${currency2}`);
          return null;
        }

        const updatedTakeProfit = {
          ...tile,
          properties: {
            ...tile.properties,
            ...updatedProperties,
          },
        };

        const patchedKeys = Object.keys(updatedProperties);
        const keysWhichShouldMakeLimitPriceReset = ['currencyPair', 'clientWay'];
        const shouldResetLimitPrice = keysWhichShouldMakeLimitPriceReset.some((key) => patchedKeys.includes(key));

        const notificationEmails = normalizeEmailsList(
          updatedTakeProfit.properties.notificationEmails ?? preferencesNotificationEmails ?? [],
        );
        const hasCustomerPriceInputChanged = isDefined(updatedProperties.customerPrice);

        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        const data = await validateTakeProfitMutation({
          tileId,

          currency1,
          currency2,
          clientId,
          way: updatedTakeProfit.properties.clientWay,
          notional: updatedTakeProfit.properties.notional,
          notionalCurrencyIndex: updatedTakeProfit.properties.notionalCurrencyIndex,
          limitPrice:
            !hasCustomerPriceInputChanged && !shouldResetLimitPrice
              ? updatedTakeProfit.properties.limitPrice
              : undefined,
          customerPrice: updatedTakeProfit.properties.customerPrice,
          margin: updatedTakeProfit.properties.commercialMargin,
          expiry: updatedTakeProfit.properties.expiry,
          notificationEmails: {
            isOverridenEmails: isDefined(updatedProperties.notificationEmails),
            emails: notificationEmails,
          },
          localTimeWithOffSet: tile.properties.clockOffset !== undefined ? Date.now() + tile.properties.clockOffset : 0,
        });

        clearTimeout(checkTimer);
        setIsChecking(false);

        return data;
      });
    },
    [tile, preferencesNotificationEmails, tileId, validateTakeProfitMutation, clientId, queue],
  );

  const validateTakeProfit = useCallback<TakeProfitValidateFn>(
    async (updatedProperties) => {
      const result = await handleValidateTakeProfit(updatedProperties);

      if (isNotDefined(result)) {
        console.error('[validate] Validation result is undefined');
        return undefined;
      }

      if ('data' in result && result.data !== undefined) {
        return { data: result.data, error: undefined };
      }

      if ('error' in result && result.error !== undefined) {
        return { data: undefined, error: result.error };
      }

      return undefined;
    },
    [handleValidateTakeProfit],
  );

  const modifyingFieldContent = useMemo(
    () => ({
      isModifyingField,
      setIsModifyingField,
    }),
    [isModifyingField],
  );

  const content = useMemo(
    () => ({
      tileId,
      isChecking,
      validateTakeProfit,
      ...modifyingFieldContent,
    }),
    [isChecking, modifyingFieldContent, tileId, validateTakeProfit],
  );

  return (
    <TakeProfitTileIdContext.Provider value={tileId}>
      <TakeProfitIsCheckingContext.Provider value={isChecking}>
        <TakeProfitValidateContext.Provider value={validateTakeProfit}>
          <TakeProfitModifyingFieldContext.Provider value={modifyingFieldContent}>
            <TakeProfitContext.Provider value={content}>{children}</TakeProfitContext.Provider>
          </TakeProfitModifyingFieldContext.Provider>
        </TakeProfitValidateContext.Provider>
      </TakeProfitIsCheckingContext.Provider>
    </TakeProfitTileIdContext.Provider>
  );
};

export function useTakeProfitTileId() {
  return useContext(TakeProfitTileIdContext);
}

export function useTakeProfitIsChecking() {
  return useContext(TakeProfitIsCheckingContext);
}

export function useValidateTakeProfit() {
  return useContext(TakeProfitValidateContext);
}

export function useTakeProfitIsModifyingField() {
  return useContext(TakeProfitModifyingFieldContext).isModifyingField;
}

export function useTakeProfitSetIsModifyingField() {
  return useContext(TakeProfitModifyingFieldContext).setIsModifyingField;
}
